import { KeyboardEvent, useMemo, useState } from 'react';
import clsx from 'clsx';
import { InputAdornment, OutlinedInput } from '@mui/material';
import { OutlinedInputProps } from '@mui/material/OutlinedInput/OutlinedInput';

import { StatusTooltip } from '../Tooltip';
import { StatusTooltipTemplate } from '../Tooltip/template';
import { IVerificationResultByRecognition } from 'common/ui/interfaces/verification-result-by-recognition';
import { VerificationResultByRecognition } from 'common/ui/constants/verification-result-by-recognition';

import { ReactComponent as CheckmarkIcon } from 'assets/images/input-checkmark.svg';
import { ReactComponent as WarningIcon } from 'assets/images/warning.svg';
import { ReactComponent as ActionIcon } from 'assets/images/action-in-input.svg';

import styles from '../styles.module.scss';

type InputProps = {
  onClickToAdornmentOrEnterKey?: () => void;
  verified?: boolean;
  verificationResult?: IVerificationResultByRecognition;
  hideVerification?: boolean;
  canApprove?: boolean;
} & OutlinedInputProps;

export const InputWithStatus = ({
  id,
  label,
  verified,
  error,
  value,
  onChange,
  onMouseEnter,
  onMouseLeave,
  verificationResult = VerificationResultByRecognition,
  onClickToAdornmentOrEnterKey,
  hideVerification = false,
  canApprove = false,
}: InputProps) => {
  const [initValue] = useState(value);

  const handleAction = () => {
    if (value) {
      onClickToAdornmentOrEnterKey && onClickToAdornmentOrEnterKey();
    }
  };

  const handleKeywordKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key == 'Enter') {
      handleAction();
    }
  };

  const showAction = useMemo(() => {
    return Boolean(
      value &&
        (canApprove || initValue !== value) &&
        onClickToAdornmentOrEnterKey
    );
  }, [value, canApprove, initValue, onClickToAdornmentOrEnterKey]);

  return (
    <OutlinedInput
      id={id}
      type="text"
      endAdornment={
        hideVerification ? (
          showAction ? (
            <InputAdornment position="end">
              <div className={styles.clicked} onClick={handleAction}>
                <ActionIcon />
              </div>
            </InputAdornment>
          ) : undefined
        ) : (
          <InputAdornment position="end">
            {showAction && (
              <div className={styles.clicked} onClick={handleAction}>
                <ActionIcon />
              </div>
            )}
            {!showAction &&
              (verified ? (
                <CheckmarkIcon />
              ) : (
                <StatusTooltip
                  title={
                    <StatusTooltipTemplate
                      title={verificationResult.title}
                      text={verificationResult.text}
                    />
                  }
                >
                  <WarningIcon />
                </StatusTooltip>
              ))}
          </InputAdornment>
        )
      }
      label={label}
      color="secondary"
      error={error}
      classes={{
        root: clsx(styles.inputWrapper, verified && styles.verified),
        input: styles.input,
        notchedOutline: styles.fieldset,
        disabled: styles.disabled,
        focused: styles.fieldset,
        error: styles.error,
      }}
      value={value}
      onChange={onChange}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={handleKeywordKeyPress}
    />
  );
};
